import { I18N } from "oc-I18N";
import { isEqualEpsilon } from "oc-commons/Commons";
import { useEffect, useRef, useState } from "react";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import { groupBy } from "lodash";

export enum CookieCategory {
  "required" = "required",
  "comfort" = "comfort",
  "advertising" = "advertising",
  "statistic" = "statistic",
}

export type CookieState = {
  key: string;
  accepted: boolean;
};

export type CookieListItem = {
  key: string;
  category: CookieCategory;
  label: string;
};

const defaultStorageKey = "cookieBanner";
const defaultCookiestate = () => {
  return { cookieStates: [], userNoticed: false };
};

export function isCookieAllowed(key, storageKey = defaultStorageKey) {
  const cookieStorage = JSON.parse(localStorage.getItem(storageKey)) ?? defaultCookiestate();
  if (!cookieStorage.userNoticed) {
    return false;
  } else {
    return cookieStorage.cookieStates.find((cookie) => cookie.key == key)?.accepted ?? false;
  }
}

export function isCookieSupressed() {
  const params = new URLSearchParams(window.location.search);
  const cookieParam = params.get("cookieBanner");
  return cookieParam === "false";
}

export async function requestCookieApproval(
  key,
  message,
  storageKey = defaultStorageKey,
  okLabel = undefined
): Promise<boolean> {
  let cookieState = JSON.parse(localStorage.getItem(storageKey)) ?? defaultCookiestate();
  let cookieInd = cookieState.cookieStates.findIndex((cookie) => cookie.key == key);
  if (cookieInd < 0) {
    return false;
  }
  if (cookieState.cookieStates[cookieInd].accepted) {
    return true;
  }
  return new Promise((resolve) => {
    Dialogs.askQuestionOkOrCancel(
      message,
      okLabel,
      () => {
        cookieState = JSON.parse(localStorage.getItem(storageKey)) ?? defaultCookiestate();
        cookieInd = cookieState.cookieStates.findIndex((cookie) => cookie.key == key);
        cookieState.cookieStates[cookieInd].accepted = true;
        localStorage.setItem(storageKey, JSON.stringify(cookieState));
        resolve(true);
      },
      () => {
        resolve(false);
      }
    );
  });
}

const CookieList = ({
  name,
  cookies,
  cookieStates,
  updateStates,
  required = false,
}: {
  name: CookieCategory;
  cookies: CookieListItem[];
  cookieStates: CookieState[];
  updateStates: (states: CookieState[]) => void;
  required?: boolean;
}) => {
  const statemap = new Map(cookieStates.map(({ key, accepted }) => [key, accepted]));
  const sendUpdate = () => {
    updateStates(
      [...statemap.entries()].map(([key, accepted]) => {
        return { key, accepted };
      })
    );
  };
  const updateKategorie = (value: boolean) => {
    cookies.forEach(({ key }) => statemap.set(key, value));
    sendUpdate();
  };
  const updateCookie = ({ key }: { key: string }, value: boolean) => {
    statemap.set(key, value);
    sendUpdate();
  };

  return (
    <div className={"cookieList"} role="group" aria-labelledby={`cookie-category-${name}`}>
      <label>
        <input
          type={"checkbox"}
          checked={cookies.some((c) => statemap.get(c.key))}
          onChange={(e) => updateKategorie(e.target.checked)}
          disabled={required}
          aria-label={I18N("aria::Cookies")}
        />
        <h2 id={`cookie-category-${name}`}>{I18N(name)}</h2>
      </label>
      <ul role="list">
        {cookies.map((c) => (
          <li key={c.key} role="listitem">
            <label>
              <input
                type={"checkbox"}
                checked={statemap.get(c.key) ?? false}
                onChange={(e) => updateCookie(c, e.target.checked)}
                disabled={required}
                aria-label={c.label}
              />
              <span>{c.label}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};
export const Cookiebanner = ({
  cookies = [],
  storageKey = defaultStorageKey,
}: {
  cookies?: CookieListItem[];
  storageKey?: string;
}) => {
  type TState = {
    cookieStates: CookieState[];
    userNoticed: boolean;
  };
  const [cookieState, setCookieState] = useState<TState>(defaultCookiestate);
  const ref = useRef<HTMLDialogElement>();

  const updateState = (state: TState) => {
    if (!isEqualEpsilon(state, cookieState, undefined, true)) {
      setCookieState(state);
    }
  };

  useEffect(() => {
    const cookieStates = JSON.parse(localStorage.getItem(storageKey)) ?? cookieState;
    if (cookieStates) {
      const statemap: Map<string, boolean> = new Map(
        cookieStates.cookieStates.map(({ key, accepted }) => [key, accepted])
      );
      const cookieMap = new Map(cookies.map((c) => [c.key, c]));
      const reqCookies = cookies.filter((c) => c.category == CookieCategory.required);
      let userNoticed = cookieStates.userNoticed;
      let hasChanges = false;
      reqCookies.forEach((r) => {
        if (!statemap.has(r.key) || !statemap.get(r.key)) {
          hasChanges = true;
          statemap.set(r.key, true);
        }
      });
      cookieStates.cookieStates.forEach(({ key }) => {
        if (!cookieMap.has(key)) {
          statemap.delete(key); //lösche alte cookies ohne neu verifikartion
        }
      });
      cookies.forEach(({ key, category }) => {
        const required = category === CookieCategory.required;
        if (!statemap.has(key) || (required && !statemap.get(key))) {
          hasChanges = true;
          statemap.set(key, required);
        }
      });
      updateState({
        cookieStates: [...statemap.entries()].map(([key, accepted]) => {
          return { key, accepted };
        }),
        userNoticed: hasChanges ? false : userNoticed,
      });
    }
  }, [storageKey, cookies]);

  useEffect(() => {
    if (!cookieState.userNoticed) {
      ref?.current?.showModal();
    } else {
      ref?.current?.close();
    }
  }, [cookieState, ref]);

  useEffect(() => {
    ref.current?.addEventListener("cancel", (ev) => ev.preventDefault());
  }, [ref.current]);

  useEffect(() => {
    if (cookieState.userNoticed) {
      localStorage.setItem(storageKey, JSON.stringify(cookieState));
      APP.trigger("cookiesSaved");
    }
  }, [cookieState, storageKey]);

  const groupedCookies = groupBy(cookies, ({ category }) => category);
  const updateCookiestates = (cookieStates: CookieState[]) =>
    updateState({ ...cookieState, cookieStates });
  const updateUserNoticed = (userNoticed: boolean) => {
    if (userNoticed != cookieState.userNoticed) {
      updateState({ ...cookieState, userNoticed });
    }
  };

  const saveSelection = () => {
    updateUserNoticed(true);
  };

  const saveRequired = () => {
    const reqCookies = cookies.map(({ key, category }) => {
      return { key, accepted: category === CookieCategory.required };
    });
    updateState({ cookieStates: reqCookies, userNoticed: true });
  };

  const saveAll = () => {
    const reqCookies = cookies.map(({ key }) => {
      return { key, accepted: true };
    });
    updateState({ cookieStates: reqCookies, userNoticed: true });
  };

  return (
    !cookieState.userNoticed &&
    cookieState.cookieStates.length > 0 && (
      <dialog
        id="cookiebanner"
        ref={ref}
        role="dialog"
        aria-labelledby="cookie-settings-title"
        aria-modal="true"
      >
        <h1 id="cookie-settings-title">{I18N("Cookie Einstellungen")}</h1>
        <div
          className={"subtext"}
          dangerouslySetInnerHTML={{ __html: I18N("Subtext mit Links zur Datenschutzerklärung") }}
        />
        <div className={"selector"}>
          {Object.keys(CookieCategory).map(
            (key) =>
              groupedCookies[key] && (
                <CookieList
                  key={key}
                  name={CookieCategory[key]}
                  required={key == CookieCategory.required}
                  cookies={groupedCookies[key]}
                  cookieStates={cookieState.cookieStates}
                  updateStates={updateCookiestates}
                />
              )
          )}
        </div>
        <div className={"buttons"}>
          <button
            className={"primary-button"}
            onClick={() => saveRequired()}
            aria-label={I18N("aria::Nur notwendige Cookies akzeptieren")}
          >
            {I18N("Nur notwendige Cookies akzeptieren")}
          </button>
          <button
            className={"primary-button"}
            onClick={() => saveSelection()}
            aria-label={I18N("aria::Cookie-Auswahl speichern")}
          >
            {I18N("Auswahl speichern")}
          </button>
          <button
            className={"primary-button"}
            onClick={() => saveAll()}
            aria-label={I18N("aria::Alle Cookies akzeptieren")}
          >
            {I18N("Alle Cookies akzeptieren")}
          </button>
        </div>
      </dialog>
    )
  );
};
